import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Location, LocationConnection, Character } from '../../types';
import { gameSocket } from '../../lib/socket';

interface LocationMarkerProps {
  location: Location;
  isCurrentLocation: boolean;
  canTravel?: boolean;
  character: Character | null;
  travelTime?: number;
  onStartTravel?: (locationId: string) => void;
  currentLocationName?: string;
}

const LocationMarker: React.FC<LocationMarkerProps> = ({ 
  location, 
  isCurrentLocation,
  canTravel = false,
  character,
  travelTime = 60,
  onStartTravel,
  currentLocationName = 'Неизвестная локация'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTravelMenu, setShowTravelMenu] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [labelOffset, setLabelOffset] = useState<number>(0);
  
  const locationNameRef = useRef<HTMLDivElement>(null);
  
  // Проверяем, путешествует ли персонаж к этой локации
  const isTravelingHere = character?.current_location_id?.type === 'traveling' &&
    character.current_location_id.to_location_id === location.id;
  
  // Таймер для путешествия
  useEffect(() => {
    if (isTravelingHere && character?.current_location_id?.type === 'traveling') {
      const updateTimer = () => {
        const now = new Date().getTime();
        const arrival = new Date((character.current_location_id as any).arrives_at).getTime();
        const remaining = Math.max(0, Math.floor((arrival - now) / 1000));
        setTimeLeft(remaining);
        
        if (remaining === 0) {
          setShowTravelMenu(false);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }
  }, [isTravelingHere, character]);
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isCurrentLocation) return;
    
    if (canTravel || isTravelingHere) {
      setShowTravelMenu(!showTravelMenu);
    }
  };
  
  const handleStartTravel = () => {
    if (onStartTravel) {
      onStartTravel(location.id);
    }
    setShowTravelMenu(false);
  };
  
  const handleCancelTravel = () => {
    gameSocket.emit('location.cancel-travel');
    setShowTravelMenu(false);
  };
  
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Вычисляем смещение для центрирования меню относительно надписи
  useEffect(() => {
    if (locationNameRef.current && (isHovered || isCurrentLocation || showTravelMenu || isTravelingHere)) {
      const width = locationNameRef.current.offsetWidth;
      setLabelOffset(width / 2);
    }
  }, [isHovered, isCurrentLocation, showTravelMenu, isTravelingHere, location.name]);
  
  // Закрытие меню при клике вне
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.location-marker-wrapper')) {
        setShowTravelMenu(false);
      }
    };
    
    if (showTravelMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTravelMenu]);
  
  // Проверяем наличие properties
  if (!location.properties) {
    return null;
  }
  
  const { clickArea, pin, label } = location.properties;
  
  return (
    <div className="location-marker-wrapper">
      {/* Область клика */}
      <div
        className={`location-marker ${isCurrentLocation ? 'current-location' : ''} ${canTravel ? 'can-travel' : ''}`}
        style={{
          left: `${clickArea.x}px`,
          bottom: `${clickArea.y}px`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
      >
        <div
          className="location-click-area"
          style={{
            width: `${clickArea.radius * 2}px`,
            height: `${clickArea.radius * 2}px`,
          }}
        />
      </div>
      
      {/* Булавка локации */}
      <div 
        className={`location-pin ${isCurrentLocation ? 'current' : ''}`}
        style={{
          left: `${pin.x}px`,
          bottom: `${pin.y}px`,
        }}
      >
        <svg width="40" height="48" viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg" overflow="visible">
          {/* Тень */}
          <ellipse cx="20" cy="46" rx="10" ry="2" fill="black" opacity="0.2"/>
          
          {/* Основная булавка */}
          <path 
            d="M20 4C11.16344 4 4 11.16344 4 20C4 28.8366 20 44 20 44C20 44 36 28.8366 36 20C36 11.16344 28.8366 4 20 4Z" 
            fill={isCurrentLocation ? "#22c55e" : "#dc2626"}
            stroke="white"
            strokeWidth="2"
          />
          
          {/* Внутренний круг */}
          <circle 
            cx="20" 
            cy="20" 
            r="8" 
            fill="white" 
            opacity="0.9"
          />
          
          {/* Центральная точка */}
          <circle 
            cx="20" 
            cy="20" 
            r="4" 
            fill={isCurrentLocation ? "#22c55e" : "#dc2626"}
          />
        </svg>
      </div>
      
      {/* Название локации */}
      {(isHovered || isCurrentLocation || showTravelMenu || isTravelingHere) && (
        <div 
          ref={locationNameRef}
          className="location-name"
          style={{
            left: `${label.x}px`,
            bottom: `${label.y}px`,
            fontSize: '12px',
          }}
        >
          {location.name}
        </div>
      )}
      
      {/* Контекстное меню путешествия */}
      {showTravelMenu && canTravel && !isTravelingHere && (
        <div 
          className="location-travel-menu"
          style={{
            left: `${label.x + labelOffset}px`,
            bottom: `${label.y + 35}px`,
          }}
        >
          <div className="travel-menu-header">
            <span className="from-location">{currentLocationName}</span>
            <span className="arrow-icon">→</span>
            <span className="to-location">{location.name}</span>
          </div>
          
          <div className="travel-menu-content">
            <div className="travel-info">
              <span className="travel-icon">🚶</span>
              <span className="travel-time">Время в пути: {travelTime} сек</span>
            </div>
            
            <div className="travel-menu-actions">
              <button className="btn-travel" onClick={handleStartTravel}>
                Перейти
              </button>
              <button className="btn-cancel" onClick={() => setShowTravelMenu(false)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Индикатор путешествия */}
      {isTravelingHere && (
        <div 
          className="location-travel-status"
          style={{
            left: `${label.x + labelOffset}px`,
            bottom: `${label.y + 35}px`,
          }}
        >
          <span className="traveling-icon">🚶</span>
          <span className="traveling-text">В пути: {formatTime(timeLeft)}</span>
        </div>
      )}
    </div>
  );
};

export default LocationMarker;
